import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  diagnoseDisplayText,
  type DiagnosisPageType,
} from "../api/diagnosis";
import { t } from "../lib/i18n";
import type { DiagnosisResponse, DiagnosisStatus } from "../types";

/** 貼り付け本文だけを対象に、表示の不足候補と人の確認箇所を分ける画面。 */
export function Diagnosis() {
  const location = useLocation();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [pageType, setPageType] = useState<DiagnosisPageType>("commerce");
  const mutation = useMutation({
    mutationFn: () => diagnoseDisplayText(text, pageType),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    mutation.mutate();
  }

  return (
    <Page
      title={t("ストア表示を診断")}
      subtitle={t("販売ページや特商法表記の本文を貼り付けて確認します")}
      backAction={{
        content: t("ホーム"),
        onAction: () => navigate({ pathname: "/", search: location.search }),
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Banner tone="info" title={t("診断できる範囲")}>
              <Text as="p">
                {t("この診断は補助です。URLを巡回せず、ここへ貼り付けた本文だけを確認します。最終的な法令適合性の判断はユーザーの責任です。")}
              </Text>
            </Banner>

            <Card>
              <form onSubmit={submit}>
                <BlockStack gap="400">
                  <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                    <Select
                      label={t("ページ種別")}
                      options={[
                        { label: t("商品・サービス販売"), value: "commerce" },
                        { label: t("ソフトウェア・アプリ販売"), value: "software" },
                      ]}
                      value={pageType}
                      onChange={(value) => {
                        mutation.reset();
                        setPageType(value as DiagnosisPageType);
                      }}
                    />
                  </InlineGrid>
                  <TextField
                    label={t("確認する本文")}
                    helpText={t("公開予定の特商法表記、商品説明、口コミ・体験談を含む本文を貼り付けてください。")}
                    value={text}
                    onChange={(value) => {
                      mutation.reset();
                      setText(value);
                    }}
                    autoComplete="off"
                    multiline={12}
                    maxLength={200_000}
                    showCharacterCount
                  />
                  <InlineStack align="end">
                    <Button variant="primary" submit loading={mutation.isPending} disabled={!text.trim()}>
                      {t("本文を診断")}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </form>
            </Card>

            {mutation.isError ? (
              <Banner tone="critical" title={t("診断に失敗しました")}>
                <Text as="p">{mutation.error instanceof Error ? mutation.error.message.replace(/^API \d+: /, "") : String(mutation.error)}</Text>
              </Banner>
            ) : null}

            {mutation.data ? (
              <DiagnosisResult
                result={mutation.data}
                onShowPlans={() => navigate({ pathname: "/plans", search: location.search })}
              />
            ) : null}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function DiagnosisResult({ result, onShowPlans }: { result: DiagnosisResponse; onShowPlans: () => void }) {
  return (
    <BlockStack gap="400">
      <Banner tone="warning" title={t("診断結果は確認補助です")}>
        <Text as="p">{t(result.disclaimer)}</Text>
      </Banner>
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">{t("確認結果")}</Text>
              <Text as="p" tone="subdued">{t("ルール版: {version} / 入力ID: {hash}", { version: result.rule_version, hash: result.input_sha256.slice(0, 12) })}</Text>
            </BlockStack>
            <InlineStack gap="200" wrap>
              <Badge tone="success">{t("確認できた {count}", { count: result.counts.present })}</Badge>
              <Badge tone="warning">{t("不足候補 {count}", { count: result.counts.missing_candidate })}</Badge>
              <Badge tone="critical">{t("人の確認 {count}", { count: result.counts.manual_review })}</Badge>
            </InlineStack>
          </InlineStack>
          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            headings={[t("区分"), t("項目"), t("判定"), t("根拠と修正案")]}
            rows={result.findings.map((finding) => [
              finding.category,
              finding.label,
              <Badge tone={statusTone(finding.status)}>{statusLabel(finding.status)}</Badge>,
              result.full_access ? (
                <BlockStack gap="100"><Text as="p" variant="bodySm">{finding.evidence}</Text><Text as="p" variant="bodySm" tone="subdued">{finding.recommendation}</Text></BlockStack>
              ) : (
                <Text as="p" variant="bodySm" tone="subdued">{t("Proで根拠と修正案を確認できます")}</Text>
              ),
            ])}
          />
        </BlockStack>
      </Card>
      {result.hidden_count > 0 ? (
        <Banner
          tone="info"
          title={t("Freeでは優先3件を表示しています")}
          action={{ content: t("料金プランを見る"), onAction: onShowPlans }}
        >
          <Text as="p">{t("全結果では、ほかに {count}件の確認項目があります。", { count: result.hidden_count })}</Text>
        </Banner>
      ) : null}
    </BlockStack>
  );
}

function statusLabel(status: DiagnosisStatus): string {
  const labels: Record<DiagnosisStatus, string> = {
    present: t("確認できた"),
    missing_candidate: t("不足候補"),
    manual_review: t("人の確認が必要"),
  };
  return labels[status];
}

function statusTone(status: DiagnosisStatus): "success" | "warning" | "critical" {
  if (status === "present") return "success";
  if (status === "manual_review") return "critical";
  return "warning";
}
